import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, exhaustMap, mergeMap, of, share, switchMap, throwError, withLatestFrom } from 'rxjs';
import { ZoneRegionsActions } from '@states/zone-regions/zone-regions.actions';
import { zoneRegionsFeature, getProcessingKeysFromSaveRequest, getZoneRegionCameraProcessingKey } from '@states/zone-regions/zone-regions.feature';
import { ZoneRegionsModels } from '@share-models';
import { ZoneRegionsService } from '../../services/zone-regions.service';
import { UtilsService } from '../../edge/utils.service';
import { LoggerService } from '../../services/logger.service';
import { ContextualLoggerService } from '../../services/contextual-logger.service';
import { SharedActions } from '@states/shared/shared.action-types';
import { HttpErrorResponse } from '@angular/common/http';

const context = 'ZoneRegionsEffect';
const createLogger = (baseLogger: LoggerService) => new ContextualLoggerService(baseLogger, context);
const toSharedError = (utilsService: UtilsService, response: HttpErrorResponse | Error) =>
  SharedActions.showMessage({ error: utilsService.errMessage(response) });

export const startLoadingZoneRegions$ = createEffect(
  (
    actions$ = inject(Actions),
  ) => {
    return actions$.pipe(
      ofType(ZoneRegionsActions.startLoadingZoneRegions),
      exhaustMap(({filters}) => [
        ZoneRegionsActions.setIsLoading({ isLoading: true }),
        ZoneRegionsActions.getZoneRegions({filters}),
      ]),
    );
  },
  { functional: true },
);

export const getZoneRegions$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    zoneRegionsService = inject(ZoneRegionsService),
    utilsService = inject(UtilsService),
    baseLogger = inject(LoggerService),
  ) => {
    const logger = createLogger(baseLogger);
    return actions$.pipe(
      ofType(ZoneRegionsActions.getZoneRegions),
      withLatestFrom(
        store.select(zoneRegionsFeature.selectZoneRegionsListState)
        ),
      switchMap(([{filters} , { isLastPage, latestTs }]) => {
        if (!isLastPage) {
          return zoneRegionsService.getZoneRegions( filters, latestTs)
            .pipe(
              switchMap(zoneRegions => {
                return [
                  ZoneRegionsActions.getZoneRegionsSuccess({ zoneRegions }),
                  ZoneRegionsActions.setIsLoading({ isLoading: false }),
                ];
              }),
              catchError(response => {
                const errorMessage = utilsService.errMessage(response);
                logger.error(errorMessage);
                return [
                  ZoneRegionsActions.setIsLoading({ isLoading: false }),
                ];
              }),
            );
        } else {
          return of(ZoneRegionsActions.setIsLoading({ isLoading: false }));
        }
      })
    );
  },
  { functional: true },
);

export const startCreateZoneRegion$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ZoneRegionsActions.startCreateZoneRegion),
      exhaustMap(({ create }) => [
        ZoneRegionsActions.setIsSaving({ isSaving: true }),
        ZoneRegionsActions.createZoneRegion({ create }),
      ]),
      share(),
    );
  },
  { functional: true },
);

export const createZoneRegion$ = createEffect(
  (
    actions$ = inject(Actions),
    zoneRegionsService = inject(ZoneRegionsService),
    translateService = inject(TranslateService),
    utilsService = inject(UtilsService),
  ) => {
    return actions$.pipe(
      ofType(ZoneRegionsActions.createZoneRegion),
      mergeMap(({ create }) =>
        zoneRegionsService.createWithResponse(create).pipe(
          switchMap(({ zoneRegion, affectedZoneRegionCameras }) => [
            ZoneRegionsActions.createZoneRegionSuccess({
              zoneRegion,
              affectedZoneRegionCameras,
            }),
            ZoneRegionsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({ success: translateService.instant('TOASTS.ZONE_REGION_CREATED') }),
          ]),
          catchError((response) => [
            toSharedError(utilsService, response),
            ZoneRegionsActions.setIsSaving({ isSaving: false }),
            ZoneRegionsActions.createZoneRegionFail(),
          ]),
        ),
      ),
      share(),
    );
  },
  { functional: true },
);

export const startUpdateZoneRegionCamera$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ZoneRegionsActions.startUpdateZoneRegionCamera),
      exhaustMap(({ zoneRegionId, cameraId, edgeId, locationId, data }) => {
        const key = getZoneRegionCameraProcessingKey(zoneRegionId, cameraId, edgeId, locationId);
        return [
          ZoneRegionsActions.addProcessingCameraKeys({ keys: [key] }),
          ZoneRegionsActions.setIsSaving({ isSaving: true }),
          ZoneRegionsActions.updateZoneRegionCamera({ zoneRegionId, cameraId, edgeId, locationId, data }),
        ];
      }),
      share(),
    );
  },
  { functional: true },
);

export const updateZoneRegionCamera$ = createEffect(
  (
    actions$ = inject(Actions),
    zoneRegionsService = inject(ZoneRegionsService),
    translateService = inject(TranslateService),
    utilsService = inject(UtilsService),
  ) => {
    return actions$.pipe(
      ofType(ZoneRegionsActions.updateZoneRegionCamera),
      mergeMap(({ zoneRegionId, cameraId, edgeId, locationId, data }) =>
        zoneRegionsService.updateCamera(zoneRegionId, cameraId, edgeId, locationId, data).pipe(
          switchMap((zoneRegionCamera) => {
            const key = getZoneRegionCameraProcessingKey(
              zoneRegionCamera.zoneRegionId,
              zoneRegionCamera.cameraId,
              zoneRegionCamera.edgeId,
              zoneRegionCamera.locationId,
            );
            return [
              ZoneRegionsActions.updateZoneRegionCameraSuccess({ zoneRegionCamera }),
              ZoneRegionsActions.removeProcessingCameraKeys({ keys: [key] }),
              ZoneRegionsActions.setIsSaving({ isSaving: false }),
              SharedActions.showMessage({ success: translateService.instant('TOASTS.ZONE_REGION_CAMERA_ZONES_UPDATED') }),
            ];
          }),
          catchError((response) => {
            const key = getZoneRegionCameraProcessingKey(zoneRegionId, cameraId, edgeId, locationId);
            return [
              toSharedError(utilsService, response),
              ZoneRegionsActions.removeProcessingCameraKeys({ keys: [key] }),
              ZoneRegionsActions.setIsSaving({ isSaving: false }),
              ZoneRegionsActions.updateZoneRegionCameraFail(),
            ];
          }),
        ),
      ),
      share(),
    );
  },
  { functional: true },
);

export const startRenameZoneRegion$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ZoneRegionsActions.startRenameZoneRegion),
      exhaustMap(({ id, name, tags }) => [
        ZoneRegionsActions.addToProcessingID({ id }),
        ZoneRegionsActions.setIsSaving({ isSaving: true }),
        ZoneRegionsActions.renameZoneRegion({ id, name, tags }),
      ]),
      share(),
    );
  },
  { functional: true },
);

export const renameZoneRegion$ = createEffect(
  (
    actions$ = inject(Actions),
    zoneRegionsService = inject(ZoneRegionsService),
    translateService = inject(TranslateService),
    utilsService = inject(UtilsService),
  ) => {
    return actions$.pipe(
      ofType(ZoneRegionsActions.renameZoneRegion),
      mergeMap(({ id, name, tags }) =>
        zoneRegionsService.rename(id, name, tags).pipe(
          switchMap((zoneRegion) => [
            ZoneRegionsActions.renameZoneRegionSuccess({ zoneRegion }),
            ZoneRegionsActions.removeFromProcessingID({ id }),
            ZoneRegionsActions.setIsSaving({ isSaving: false }),
            SharedActions.showMessage({ success: translateService.instant('TOASTS.ZONE_REGION_RENAMED') }),
          ]),
          catchError((response) => [
            toSharedError(utilsService, response),
            ZoneRegionsActions.removeFromProcessingID({ id }),
            ZoneRegionsActions.setIsSaving({ isSaving: false }),
            ZoneRegionsActions.renameZoneRegionFail(),
          ]),
        ),
      ),
      share(),
    );
  },
  { functional: true },
);

export const startSetZoneCounting$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(ZoneRegionsActions.startSetZoneCounting),
      exhaustMap(({ id, countingEnabled }) => [
        ZoneRegionsActions.addToProcessingID({ id }),
        ZoneRegionsActions.setIsSaving({ isSaving: true }),
        ZoneRegionsActions.setZoneCounting({ id, countingEnabled }),
      ]),
      share(),
    );
  },
  { functional: true },
);

export const setZoneCounting$ = createEffect(
  (
    actions$ = inject(Actions),
    zoneRegionsService = inject(ZoneRegionsService),
    translateService = inject(TranslateService),
    utilsService = inject(UtilsService),
  ) => {
    return actions$.pipe(
      ofType(ZoneRegionsActions.setZoneCounting),
      mergeMap(({ id, countingEnabled }) =>
        zoneRegionsService.setZoneCounting(id, countingEnabled).pipe(
          switchMap(({ zoneRegion, failedCameraOps }) => [
            ZoneRegionsActions.setZoneCountingSuccess({ zoneRegion }),
            ZoneRegionsActions.removeFromProcessingID({ id }),
            ZoneRegionsActions.setIsSaving({ isSaving: false }),
            failedCameraOps?.length
              ? SharedActions.showMessage({ warning: translateService.instant('TOASTS.ZONE_REGION_COUNTING_PARTIAL') })
              : SharedActions.showMessage({ success: translateService.instant('TOASTS.ZONE_REGION_COUNTING_UPDATED') }),
          ]),
          catchError((response) => [
            toSharedError(utilsService, response),
            ZoneRegionsActions.removeFromProcessingID({ id }),
            ZoneRegionsActions.setIsSaving({ isSaving: false }),
            ZoneRegionsActions.setZoneCountingFail({ id, countingEnabled }),
          ]),
        ),
      ),
      share(),
    );
  },
  { functional: true },
);

export const startDeleteZoneRegion$ = createEffect(
  (
    actions$ = inject(Actions),
  ) => {
    return actions$.pipe(
      ofType(ZoneRegionsActions.startDeleteZoneRegion),
      exhaustMap(({ id }) => [
        ZoneRegionsActions.addToProcessingID({ id }),
        ZoneRegionsActions.deleteZoneRegion({ id }),
      ]),
      share(),
    );
  },
  { functional: true },
);

export const deleteZoneRegion$ = createEffect(
  (
    actions$ = inject(Actions),
    zoneRegionsService = inject(ZoneRegionsService),
    translateService = inject(TranslateService),
    utilsService = inject(UtilsService),
  ) => {
    return actions$.pipe(
      ofType(ZoneRegionsActions.deleteZoneRegion),
      mergeMap(({ id }) =>
        zoneRegionsService.delete(id)
          .pipe(
            switchMap((response) => {
              if (!response.deletedZoneRegion || response.failedCameraOps?.length) {
                return throwError(() => new Error('Zone region deletion was not completed'));
              }
              return [
                ZoneRegionsActions.deleteZoneRegionSuccess({ id: id }),
                ZoneRegionsActions.removeFromProcessingID({ id }),
                SharedActions.showMessage({ success: translateService.instant('TOASTS.ZONE_REGION_DELETED') }),
              ];
            }),
            catchError(response => {
              return [
                ZoneRegionsActions.deleteZoneRegionFail(),
                ZoneRegionsActions.removeFromProcessingID({ id }),
                toSharedError(utilsService, response),
              ];
            }),
          ),
      ),
      share(),
    );
  },
  { functional: true },
);

// ── Zone region cameras (merged from zone-region-cameras.effects) ─────────────

export const startLoadingZoneRegionCameras$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ZoneRegionsActions.startLoadingZoneRegionCameras),
      exhaustMap(({ zoneRegionId, filters }) => [
        ZoneRegionsActions.loadZoneRegionCameras({ zoneRegionId, filters }),
      ]),
    ),
  { functional: true },
);

export const startDeleteZoneRegionCamera$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ZoneRegionsActions.startDeleteZoneRegionCamera),
      exhaustMap(({ zoneRegionId, cameraId, edgeId, locationId }) => {
        const request: ZoneRegionsModels.SaveZoneRegionWithCamerasBody = {
          items: [
            {
              zoneRegionId,
              cameras: [{ cameraId, edgeId, locationId, data: [] }],
            },
          ],
        };
        return [ZoneRegionsActions.startSaveZoneRegionsWithCameras({ request, toastContext: 'zone-region-camera-deleted' })];
      }),
    ),
  { functional: true },
);

export const loadZoneRegionCameras$ = createEffect(
  (
    actions$ = inject(Actions),
    zoneRegionsService = inject(ZoneRegionsService),
  ) =>
    actions$.pipe(
      ofType(ZoneRegionsActions.loadZoneRegionCameras),
      switchMap(({ zoneRegionId, filters }) =>
        zoneRegionsService.getZoneRegionCameras(zoneRegionId, filters).pipe(
          switchMap((cameras) => [
            ZoneRegionsActions.loadZoneRegionCamerasSuccess({ zoneRegionId, cameras }),
          ]),
          catchError(() => of(ZoneRegionsActions.loadZoneRegionCamerasFail({ zoneRegionId }))),
        ),
      ),
    ),
  { functional: true },
);

export const startSaveZoneRegionsWithCameras$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ZoneRegionsActions.startSaveZoneRegionsWithCameras),
      exhaustMap(({ request, toastContext }) => [
        ZoneRegionsActions.saveZoneRegionsWithCameras({ request, toastContext }),
      ]),
    ),
  { functional: true },
);

export const saveZoneRegionsWithCameras$ = createEffect(
  (
    actions$ = inject(Actions),
    zoneRegionsService = inject(ZoneRegionsService),
    utilsService = inject(UtilsService),
    translateService = inject(TranslateService),
  ) =>
    actions$.pipe(
      ofType(ZoneRegionsActions.saveZoneRegionsWithCameras),
      mergeMap(({ request, toastContext }) =>
        zoneRegionsService.saveZoneRegionsWithCameras(request).pipe(
          switchMap((response) => {
            const toastMessageKeys: Record<string, string> = {
              'zone-region-camera-deleted': 'TOASTS.ZONE_REGION_CAMERA_DELETED',
              'zone-region-detail': 'TOASTS.ZONE_REGION_CAMERAS_UPDATED',
              'camera-edit': 'TOASTS.ZONE_REGIONS_UPDATED',
            };
            const messageKey = (toastContext && toastMessageKeys[toastContext]) ?? 'TOASTS.ZONE_REGIONS_UPDATED';
            const processingKeysToRemove = getProcessingKeysFromSaveRequest(request);
            return [
              ZoneRegionsActions.saveZoneRegionsWithCamerasSuccess({
                response,
                processingKeysToRemove,
              }),
              SharedActions.showMessage({
                success: translateService.instant(messageKey),
              }),
            ];
          }),
          catchError((response) => {
            const processingKeysToRemove = getProcessingKeysFromSaveRequest(request);
            return [
              ZoneRegionsActions.saveZoneRegionsWithCamerasFail({ processingKeysToRemove }),
              SharedActions.showMessage({ error: utilsService.errMessage(response) }),
            ];
          }),
        ),
      ),
    ),
  { functional: true, dispatch: true },
);
